'use client';

import { useOverlayTriggerState } from '@react-stately/overlays';
import { ErrorBoundary } from '@sentry/nextjs';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

import {
  AlarmAdd,
  ArrowLeft,
  ArrowLeftSmall,
  CheckOutlined,
  Flag,
  Share,
  Trophy,
  Versus,
} from '@zoomit/icons';
import { Box, cn, Container, Flex, IconButton, Link, Spacing } from '@zoomit/primitives';
import type {
  BuyingGuideResponseItem,
  GalleryItem} from '@zoomit/web-kit';
import {
  isMobile,
  OriginURL,
  PriceNotifyModal,
  ProductMediaPreviewMobile,
  ReviewRatingBox,
  SharePopover,
  usePriceNotify,
  useReviewInfo,
  UsersReviewBox,
  ZoomitReviewBox,
} from '@zoomit/web-kit';
import { Breadcrumbs, Button, cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { LightColors } from '../../constants/colors';
import { availabilityZoneLabels, SCORE_ID } from '../../constants/single-product';
import type { ProductInfo, SingleProduct } from '../../types/single-product';
import { SthWentWrong } from '../error-states/sth-went-wrong';
import ProblemReportPopup from '../shared/ProblemReportPopup';
import BuyingGuideDetailsMobileSheet from './BuyingGuideDetailsMobileSheet';
import BuyingGuideDetailsPopup from './BuyingGuideDetailsPopup';
import PriceChart from './PriceChart';
import PriceChartMobile from './PriceChartMobile';
import styles from './ProductHeader.module.scss';
import ProductMediaPreviewDesktop from './ProductMediaPreviewDesktop';

type Props = {
  id: string;
  title: string;
  slug: string;
  englishTitle: string;
  hasPriceChart: boolean;
  gallery: GalleryItem[];
  priceFrom: number;
  priceTo: number;
  inStock: boolean;
  overview: ProductInfo['overview'];
  reviews: SingleProduct['userReviewsInfo'];
  category: ProductInfo['category'];
  brand: ProductInfo['brand'];
  availabilityZone: ProductInfo['globalAvailabilityZone'];
  isPreview?: boolean;
  minReviewCharacter?: number;
  buyingGuides: BuyingGuideResponseItem[];
};

const ProductHeader = (props: Props) => {
  const {
    id,
    title,
    slug,
    englishTitle,
    priceFrom,
    priceTo,
    inStock,
    overview,
    reviews,
    brand,
    category,
    availabilityZone,
    hasPriceChart,
    isPreview = false,
    minReviewCharacter,
    buyingGuides,
    gallery,
  } = props;

  const [showShare, setShowShare] = useState(false);

  const { isAuthenticated } = useAuth();
  const { data: reviewData } = useReviewInfo({
    topicId: id,
    enabled: isAuthenticated,
    topicType: 'Product',
  });

  const problemReportPopupState = useOverlayTriggerState({});

  const { priceNotifyPopOverState, notifyQuery, deleteMutation, postMutation } = usePriceNotify({
    productId: id,
  });

  const hasOverview = overview && overview.score > 0;

  const breadcrumbOptions = [
    { title: 'زوبین', link: '/' },
    { title: `قیمت ${category.title}`, link: `/list/${category.slug}/` },
    { title: `قیمت ${category.title} ${brand.title}`, link: `/list/${category.slug}/${brand.slug}/` },
  ];
  const buyingGuideDetailsPopupState = useOverlayTriggerState({});
  const router = useRouter();

  const moreComparesLink = `/compare/${category.slug}/?products=${slug}`;

  const sortedGallery = [...gallery].sort((a, b) => {
    if (a.type === 'Video' && b.type === 'Image') return -1;
    if (a.type === 'Image' && b.type === 'Video') return 1;
    return 0;
  });

  return (
    <>
      {isMobile ? (
        <Box backgroundColor={cssVar(theme.background2)}>
          {!isPreview && (
            <Box spacing={{ px: 'x4' }}>
              <Breadcrumbs
                crumbs={breadcrumbOptions}
                separator={<ArrowLeftSmall fill={LightColors.grey5} size="sm" />}
                render={({ title, link }) => <CrumbLink href={link || ''}>{title}</CrumbLink>}
                spacing={{ m: 'x0', py: 'x2' }}
              />
            </Box>
          )}
          <Box spacing={{ px: 'x4' }}>
            <ErrorBoundary fallback={<SthWentWrong />}>
              <ProductMediaPreviewMobile productId={id} galleryItems={gallery} />
            </ErrorBoundary>
          </Box>

          <Box spacing={{ pt: 'x', pb: 'x6', px: 'x4' }}>
            <Container>
              {availabilityZone !== 'Supplied' && (
                <div className="flex justify-center">
                  <span
                    className={cn(
                      'mb-2 inline-block w-auto rounded-full px-2 leading-loose font-bold text-white',
                      availabilityZone === 'ComingSoon' ? 'bg-warning1' : 'bg-primary1',
                    )}
                  >
                    {availabilityZoneLabels[availabilityZone]}
                  </span>
                </div>
              )}
              {buyingGuides.length > 0 && (
                <div
                  className={cn(
                    'bg-[linear-gradient(to_right,rgba(239,209,67,0)_0%,rgba(186,235,48,0.20)_100%)]',
                    'mb-4 flex min-h-[30px] items-center justify-between gap-2 rounded-sm px-2 py-1',
                  )}
                >
                  <Flex gap={Spacing.x2}>
                    <Typography tag="span" fontSize={1.2} className={'text-text'} fontWeight="bold">
                      پیشنهاد زوبین
                    </Typography>
                  </Flex>
                  <Button
                    onClick={buyingGuideDetailsPopupState.open}
                    leftIcon={<ArrowLeft size="sm" className="!fill-primary1" />}
                    variant="text"
                    size="auto"
                    spacing={{ px: 'x0' }}
                  >
                    مشاهده سایر پیشنهادها
                  </Button>
                </div>
              )}
              <Typography tag="h1" variant="h4" textAlign="center" lineHeight={1.78} spacing={{ pb: 'x2' }}>
                {title}
              </Typography>
              <ErrorBoundary fallback={<SthWentWrong />}>
                {!isPreview && (
                  <PriceChartMobile
                    productId={id}
                    priceFrom={priceFrom}
                    priceTo={priceTo}
                    inStock={inStock}
                    hasPriceChart={hasPriceChart}
                    title={title}
                    image={gallery[0].image!}
                  />
                )}
              </ErrorBoundary>
              <div className="mt-4 flex gap-2">
                {!isPreview && (
                  <>
                    <div className={hasOverview ? 'w-4/12' : 'w-1/2'}>
                      <UsersReviewBox
                        countOfRates={reviews.usersCount}
                        rate={String(reviews.averageScore)}
                        href={`#${SCORE_ID}`}
                      />
                    </div>
                    <div className={hasOverview ? 'w-4/12' : 'w-1/2'}>
                      <ReviewRatingBox
                        minCharacter={minReviewCharacter}
                        slug={slug}
                        id={id}
                        title={title}
                        cover={gallery[0].image!}
                        reviewData={reviewData}
                      />
                    </div>
                  </>
                )}
                {hasOverview && (
                  <div className="w-4/12">
                    <ZoomitReviewBox score={overview.score} reviewArticleUrl={overview.reviewArticleUrl} />
                  </div>
                )}
              </div>
            </Container>
          </Box>
          <BuyingGuideDetailsMobileSheet
            categorySlug={category.slug}
            isOpen={buyingGuideDetailsPopupState.isOpen}
            onClose={buyingGuideDetailsPopupState.close}
            items={buyingGuides}
          />
        </Box>
      ) : (
        <Box
          backgroundColor={cssVar(theme.background2)}
          spacing={{ py: 'x4' }}
          style={{ borderBottom: `1px solid ${cssVar(theme.grey6)}` }}
        >
          <Container>
            {!isPreview && (
              <Breadcrumbs
                crumbs={breadcrumbOptions}
                separator={<ArrowLeftSmall fill={LightColors.grey5} size="sm" />}
                render={({ title, link }) => <CrumbLink href={`${link}`}>{title}</CrumbLink>}
                spacing={{ mb: 'x4' }}
              />
            )}
            <div className={styles.header_column}>
              <div className="preview">
                <ErrorBoundary fallback={<SthWentWrong />}>
                  <ProductMediaPreviewDesktop key={id} productId={id} gallery={sortedGallery} />
                </ErrorBoundary>
              </div>
              <div className="details relative">
                {availabilityZone !== 'Supplied' && (
                  <span
                    className={cn(
                      'mb-2 inline-block w-auto rounded-full px-2 leading-loose font-bold text-white',
                      availabilityZone === 'ComingSoon' ? 'bg-warning1' : 'bg-primary1',
                    )}
                  >
                    {availabilityZoneLabels[availabilityZone]}
                  </span>
                )}
                <Typography tag="h1" variant="h3" lineHeight={1.6}>
                  {title}
                </Typography>
                <Typography
                  tag="h2"
                  variant="h5"
                  lineHeight={1.6}
                  textColor={cssVar(theme.grey1)}
                  spacing={{ mt: 'x2' }}
                >
                  {englishTitle}
                </Typography>
                {buyingGuides.length > 0 && (
                  <div
                    className={cn(
                      'bg-[linear-gradient(to_right,rgba(239,209,67,0)_0%,rgba(186,235,48,0.20)_100%)]',
                      'mt-4 flex items-center rounded-sm px-2 py-1',
                    )}
                  >
                    <Trophy className={'fill-primary3'} />
                    <Typography spacing={{ mr: 'x2' }} tag="span" fontSize={1.2}>
                      زوبین‌پسند؛ زوبین این محصول را پیشنهاد می‌کند.
                    </Typography>
                    <IconButton onClick={buyingGuideDetailsPopupState.open}>
                      <Typography
                        spacing={{ mr: 'x' }}
                        fontSize={1.2}
                        fontWeight="bold"
                        textColor={cssVar(theme.primary2)}
                      >
                        مشاهده سایر پیشنهادها
                      </Typography>
                    </IconButton>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-4">
                  {!isPreview && (
                    <>
                      <UsersReviewBox
                        href={`#${SCORE_ID}`}
                        countOfRates={reviews.usersCount}
                        rate={String(reviews.averageScore)}
                      />
                      <ReviewRatingBox
                        minCharacter={minReviewCharacter}
                        slug={slug}
                        title={title}
                        cover={gallery[0].image!}
                        id={id}
                        reviewData={reviewData}
                      />
                    </>
                  )}
                  {overview.score > 0 && (
                    <ZoomitReviewBox score={overview.score} reviewArticleUrl={overview.reviewArticleUrl} />
                  )}
                </div>
              </div>
              {!isPreview && (
                <div className="actions flex items-center gap-8">
                  {notifyQuery.data ? (
                    <ActionButton
                      variant="text"
                      size="fit"
                      fontWeight="normal"
                      rightIcon={<CheckOutlined className="!fill-success1" size="sm" />}
                      onClick={() => deleteMutation.mutate()}
                      disabled={deleteMutation.isLoading}
                    >
                      <Typography textColor={cssVar(theme.success1)}>اطلاع‌رسانی قیمت</Typography>
                    </ActionButton>
                  ) : (
                    <ActionButton
                      variant="text"
                      size="fit"
                      fontWeight="normal"
                      rightIcon={<AlarmAdd className="!fill-grey1" size="sm" />}
                      onClick={priceNotifyPopOverState.open}
                    >
                      <Typography textColor={cssVar(theme.text)} fontSize={1.3} style={{ cursor: 'pointer' }}>
                        اطلاع‌رسانی قیمت
                      </Typography>
                    </ActionButton>
                  )}

                  <ActionButton
                    variant="text"
                    size="fit"
                    fontWeight="normal"
                    rightIcon={<Versus className="!fill-grey1" size="sm" />}
                    onClick={() => router?.push(moreComparesLink)}
                  >
                    مقایسه
                  </ActionButton>

                  <ActionButton
                    variant="text"
                    size="fit"
                    fontWeight="normal"
                    rightIcon={<Flag className="!fill-grey1" size="sm" />}
                    onClick={problemReportPopupState.open}
                  >
                    گزارش ایراد
                  </ActionButton>
                  <div className="flex">
                    <SharePopover
                      url={`${OriginURL}/product/${slug}/`}
                      title={title}
                      placement={'bottom'}
                      showShare={showShare}
                      setShowShare={setShowShare}
                    >
                      <ActionButton
                        variant="text"
                        size="fit"
                        fontWeight="normal"
                        rightIcon={<Share className="!fill-grey1" size="sm" />}
                        onClick={() => setShowShare(true)}
                      >
                        اشتراک‌گذاری
                      </ActionButton>
                    </SharePopover>
                  </div>
                </div>
              )}
              <div className="chart">
                <ErrorBoundary fallback={<SthWentWrong />}>
                  {!isPreview && (
                    <PriceChart
                      productId={id}
                      priceFrom={priceFrom}
                      priceTo={priceTo}
                      inStock={inStock}
                      hasPriceChart={hasPriceChart}
                      title={title}
                      image={gallery[0].image!}
                    />
                  )}
                </ErrorBoundary>
              </div>
            </div>
          </Container>
          {buyingGuideDetailsPopupState.isOpen && (
            <BuyingGuideDetailsPopup
              onClose={buyingGuideDetailsPopupState.close}
              categorySlug={category.slug}
              items={buyingGuides}
            />
          )}
        </Box>
      )}
      {!isPreview && (
        <>
          <ProblemReportPopup
            productId={id}
            productTitle={title}
            problemReportPopupState={problemReportPopupState}
          />
          <PriceNotifyModal
            productId={id}
            title={title}
            image={gallery[0].image!}
            onClose={priceNotifyPopOverState.close}
            isPriceNotifyShow={priceNotifyPopOverState.isOpen}
            onSetNotify={() => postMutation.mutate()}
          />
        </>
      )}
    </>
  );
};

export default ProductHeader;

const ActionButton = styled(Button)`
  padding: 0;
  font-size: 13px;

  div {
    justify-content: flex-start;
  }
`;

const CrumbLink = styled(Link)`
  color: var(${theme.text});
  font-size: 1.2rem;
  line-height: 2;
`;
